import React, { useMemo } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { makeStyles } from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MAX_DOTS = 4

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  weekHeader: {
    display: 'grid',
    paddingBottom: theme.spacing(0.5)
  },
  weekHeaderCell: {
    textAlign: 'center',
    color: theme.paintscout.palette.brand.Neutral.P60,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1,
    padding: theme.spacing(0.75, 0),
    '&:first-child, &:last-child': {
      color: theme.paintscout.palette.brand.Neutral.P75
    }
  },
  grid: {
    display: 'grid',
    gridAutoRows: '1fr',
    flex: 1,
    minHeight: 0
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    minHeight: '72px',
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  },
  selectedCell: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96
  },
  dayNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: theme.spacing(0, 0.5),
    borderRadius: theme.borderRadius.md,
    color: theme.paintscout.palette.brand.Neutral.P30,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1
  },
  offRange: {
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  todayDayNumber: {
    backgroundColor: theme.paintscout.palette.primary,
    color: theme.palette.common.white,
    fontWeight: 600
  },
  selectedDayNumber: {},
  dotsBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minHeight: '6px'
  },
  dotsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px'
  },
  dot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%'
  },
  moreLabel: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 500,
    lineHeight: 1
  }
}))

interface MobileMonthCalendarProps {
  events: CalendarEvent[]
  date: Date
  onSelectDate: (date: Date) => void
  hideWeekends: boolean
}

export default function MobileMonthCalendar({ events, date, onSelectDate, hideWeekends }: MobileMonthCalendarProps) {
  const classes = useStyles()

  const cells = useMemo(() => {
    const monthStart = moment(date).startOf('month')
    const gridStart = monthStart.clone().startOf('week')
    const monthEnd = moment(date).endOf('month')
    const gridEnd = monthEnd.clone().endOf('week')
    const days: { day: moment.Moment; offRange: boolean; events: CalendarEvent[] }[] = []
    const cursor = gridStart.clone()
    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      const dayClone = cursor.clone()
      const dow = dayClone.day()
      if (hideWeekends && (dow === 0 || dow === 6)) {
        cursor.add(1, 'day')
        continue
      }
      const offRange = !dayClone.isSame(monthStart, 'month')
      const dayEvents = events.filter((event) => {
        if (!event.start || !event.end) return false
        return moment(event.start).isSameOrBefore(dayClone, 'day') && moment(event.end).isSameOrAfter(dayClone, 'day')
      })
      days.push({ day: dayClone, offRange, events: dayEvents })
      cursor.add(1, 'day')
    }
    return days
  }, [date, events, hideWeekends])

  const today = moment()
  const selected = moment(date)
  const labels = hideWeekends ? DAY_INITIALS.slice(1, 6) : DAY_INITIALS
  const gridTemplateColumns = hideWeekends ? 'repeat(5, 1fr)' : 'repeat(7, 1fr)'

  return (
    <div className={classes.root}>
      <div className={classes.weekHeader} style={{ gridTemplateColumns }}>
        {labels.map((label, idx) => (
          <div key={`${label}-${idx}`} className={classes.weekHeaderCell}>
            {label}
          </div>
        ))}
      </div>
      <div className={classes.grid} style={{ gridTemplateColumns }}>
        {cells.map(({ day, offRange, events: dayEvents }) => {
          const isToday = day.isSame(today, 'day')
          const isSelected = day.isSame(selected, 'day')
          const dotColors = dayEvents.map((event) => event.estimator?.color).filter(Boolean) as string[]
          const visibleDots = dotColors.slice(0, MAX_DOTS)
          const overflow = dayEvents.length - visibleDots.length

          return (
            <button
              key={day.toISOString()}
              type="button"
              className={classNames(classes.cell, { [classes.selectedCell]: isSelected && !isToday })}
              onClick={() => onSelectDate(day.toDate())}
            >
              <span
                className={classNames(classes.dayNumber, {
                  [classes.offRange]: offRange,
                  [classes.todayDayNumber]: isToday,
                  [classes.selectedDayNumber]: isSelected && !isToday
                })}
              >
                {day.format('D')}
              </span>
              <div className={classes.dotsBlock}>
                <div className={classes.dotsRow}>
                  {visibleDots.map((color, idx) => (
                    <span key={idx} className={classes.dot} style={{ backgroundColor: color }} />
                  ))}
                </div>
                {overflow > 0 && <span className={classes.moreLabel}>+{overflow}</span>}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
